/**
 * Локальное состояние интерфейса. Сюда НЕ попадают сущности сервера —
 * они живут только в кэше RTK Query (§3.1: серверное состояние отделено
 * от локального). Здесь тема, открытые панели, состояние соединения.
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { type ConnectionState, isTheme, type Theme } from "@taskflow/client-core";


export type { Theme } from "@taskflow/client-core";

const THEME_STORAGE_KEY = "tf.theme";

function readStoredTheme(): Theme {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    if (isTheme(value)) return value;
  } catch {
    // Приватный режим или заблокированное хранилище — остаётся системная тема.
  }
  return "SYSTEM";
}

interface UiState {
  theme: Theme;
  connection: ConnectionState;
  /** true после события sync.lost: данные могли устареть, пока не было связи. */
  staleAfterReconnect: boolean;
  globalSearchOpen: boolean;
}

const initialState: UiState = {
  theme: readStoredTheme(),
  connection: "idle",
  staleAfterReconnect: false,
  globalSearchOpen: false,
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    themeChanged(state, action: PayloadAction<Theme>) {
      state.theme = action.payload;
      try {
        localStorage.setItem(THEME_STORAGE_KEY, action.payload);
      } catch {
        // Тема не сохранится между сессиями — это допустимая деградация,
        // а не повод падать.
      }
      const root = document.documentElement;
      if (action.payload === "SYSTEM") {
        delete root.dataset.theme;
      } else {
        root.dataset.theme = action.payload.toLowerCase();
      }
    },
    connectionChanged(state, action: PayloadAction<ConnectionState>) {
      state.connection = action.payload;
    },
    syncLost(state) {
      state.staleAfterReconnect = true;
    },
    syncRecovered(state) {
      state.staleAfterReconnect = false;
    },
    globalSearchToggled(state, action: PayloadAction<boolean>) {
      state.globalSearchOpen = action.payload;
    },
  },
});

export const {
  themeChanged,
  connectionChanged,
  syncLost,
  syncRecovered,
  globalSearchToggled,
} = uiSlice.actions;
export const uiReducer = uiSlice.reducer;
